import {
	CreateCustomFieldData,
	CustomFieldData,
	customFieldIndexQueryKey,
	Location,
	useCustomFieldDestroy,
	useCustomFieldIndex,
	useCustomFieldStore,
} from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'
import { useSetAtom } from 'jotai'
import { Trash2Icon } from 'lucide-react'
import { useRef, useState } from 'react'
import { appendToastAtom, useToastStoreContext } from '../../../..'
import { Button } from '../../../../rapid-ui'
import { ConfirmationDialog, type ConfirmationDialogRef } from '../../../../rapid-ui/structural'
import { CreateCustomFieldDialog } from './CreateCustomFieldDialog'

interface CustomFieldSectionProps {
	organisationId: string
}

export function CustomFieldSection({ organisationId }: CustomFieldSectionProps) {
	const queryClient = useQueryClient()

	const [showCreateDialog, setShowCreateDialog] = useState(false)
	const deleteDialogRef = useRef<ConfirmationDialogRef>(null)
	const toastStore = useToastStoreContext()
	const appendToast = useSetAtom(appendToastAtom, { store: toastStore })

	const { data, isPending, isLoading, isError, error } = useCustomFieldIndex(organisationId)

	const createMutation = useCustomFieldStore()
	const deleteMutation = useCustomFieldDestroy()

	const customFields = data?.fields ?? []
	const hasCustomFields = customFields.length > 0

	const handleCreate = async (data: CreateCustomFieldData) => {
		try {
			await createMutation.mutateAsync({ organisation: organisationId, data })
			setShowCreateDialog(false)

			queryClient.invalidateQueries({
				queryKey: customFieldIndexQueryKey(organisationId),
			})
		} catch (err) {
			// Error is handled in the dialog
		}
	}

	const handleDelete = async (field: CustomFieldData) => {
		try {
			await deleteDialogRef.current?.confirm({
				title: t('settings.customFields.delete.title'),
				message: t('settings.customFields.delete.message', { name: field.name }),
				confirmText: t('settings.customFields.delete.confirm'),
				cancelText: t('settings.customFields.delete.cancel'),
			})
		} catch {
			return
		}

		try {
			await deleteMutation.mutateAsync({ organisation: organisationId, field: field.id })
			queryClient.invalidateQueries({
				queryKey: customFieldIndexQueryKey(organisationId),
			})
		} catch (err) {
			appendToast({
				title: t('settings.customFields.delete.failed'),
				description: err instanceof Error ? err.message : undefined,
				variant: 'error',
				cause: err,
			})
		}
	}

	if (isPending || isLoading) {
		return (
			<div className="space-y-4">
				<div className="text-sm text-gray-500">{t('common.loading')}</div>
			</div>
		)
	}

	if (isError) {
		return (
			<div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
				<div className="font-medium text-red-900">{t('settings.customFields.failedToLoad')}</div>
				<div className="text-red-700">{error.message}</div>
			</div>
		)
	}

	if (!hasCustomFields) {
		return (
			<>
				<div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
					<div className="text-gray-500">{t('settings.customFields.noCustomFieldsConfigured')}</div>
					<div className="mt-4">
						<Button type="committal" onClick={() => setShowCreateDialog(true)} prefix="plus">
							{t('settings.customFields.add')}
						</Button>
					</div>
				</div>

				<CreateCustomFieldDialog
					open={showCreateDialog}
					onClose={() => setShowCreateDialog(false)}
					onSubmit={handleCreate}
					isLoading={createMutation.isPending}
					error={createMutation.error}
				/>
			</>
		)
	}

	return (
		<>
			<div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
				{customFields.map((field) => (
					<CustomField
						key={field.id}
						field={field}
						onDelete={() => handleDelete(field)}
					/>
				))}
			</div>

			<div className="mt-4">
				<Button type="committal" onClick={() => setShowCreateDialog(true)} prefix="plus">
					{t('settings.customFields.add')}
				</Button>
			</div>

			<CreateCustomFieldDialog
				open={showCreateDialog}
				onClose={() => setShowCreateDialog(false)}
				onSubmit={handleCreate}
				isLoading={createMutation.isPending}
				error={createMutation.error}
			/>

			<ConfirmationDialog ref={deleteDialogRef} />
		</>
	)
}

interface CustomFieldRowProps {
	field: CustomFieldData
	onDelete: () => void
}

function CustomField({ field, onDelete }: CustomFieldRowProps) {
	const { name, type, isRequired, location } = field

	return (
		<div
			className={ctl(`group flex items-center gap-4 p-4 transition-colors bg-white hover:bg-gray-50`)}
		>
			<div className="flex flex-col gap-1 flex-1 min-w-0">
				<span className="text-gray-900 font-medium truncate">
					{name}
				</span>
				<div className="flex items-center gap-2 text-xs text-gray-500">
					<span className="font-semibold uppercase tracking-wide text-gray-600">
						{type}
					</span>
					<span className="text-gray-300">&bull;</span>
					<span className="capitalize">{location}</span>
					<span className="text-gray-300">&bull;</span>
					<span>
						{isRequired ? t('settings.customFields.required') : t('settings.customFields.optional')}
					</span>
				</div>
			</div>

			<button
				type="button"
				onClick={onDelete}
				aria-label={t('settings.customFields.delete.action')}
				className={ctl(`
					flex items-center justify-center
					size-8 rounded-md
					text-gray-300 hover:text-red-600 hover:bg-red-50
					opacity-0 group-hover:opacity-100 focus:opacity-100
					transition-all cursor-pointer
					appearance-none border-none bg-transparent
				`)}
			>
				<Trash2Icon className="size-4" />
			</button>
		</div>
	)
}
